import React, { useEffect, useState } from "react";

import { useHistory } from "react-router-dom";
import { FileText, HelpCircle } from "react-feather";

import Button from "../../shared/Button";
import Card from "../../shared/Card";
import CustomSelect from "../../shared/CustomSelect";
import SnackbarAlert from "../../shared/SnackbarAlert";

import RequestCalculationServices from "../../requests/calculation/request.calculation.services.component";
import SelectModality from "../../requests/fields/select.modality.component";

import requestFieldsConfig from "../../requests/fields/request.fields.config";
import { requestServices } from "../../../domain/constant/request.services.constant";
import useRequest from "../../../hooks/useRequest";
import { duplicateOf, resolveDuplicate } from "../../requests/linked/linked.duplicate.dialog";


import requestRegistryService from "../../../domain/services/solicitation/request.registry.service";
import requestServicesService from "../../../domain/services/solicitation/request.services.service";
import solicitationService from "../../../domain/services/solicitation/solicitation.service";
import requestLinkedService from "../../../domain/services/request/linked/request.linked.service";
import loginService from "../../../domain/services/login.service";
import communicationProvider from "../../../providers/communication.provider";

// Peças que não cabem como mais um serviço desta solicitação (ex.: a 2ª petição) seguem
// automaticamente para uma solicitação nova, já preenchida — o cliente só escolhe a peça.
const LINKABLE_SERVICE_KEYS = [
    requestServices.PETITION,
    requestServices.ANALYSIS,
    requestServices.NOTION,
    requestServices.CONTRACT,
    requestServices.EXTRAJUDICIALDOCUMENT,
];

export default function CorrectionNewServiceForm({
    request,
    services = [],
    onCancel,
}) {

    const mainRequestId = request.mainId || request.id;

    const history = useHistory();

    const [createNewService, isCreatingNewService] = useRequest(requestServicesService.create);
    const [createLinkedRequest, isCreatingLinkedRequest] = useRequest(requestLinkedService.createLinkedRequest);
    const isCreatingServices = isCreatingNewService || isCreatingLinkedRequest;

    const [newService, setNewService] = useState({});
    const [additionalFields, setAdditionalFields] = useState([]);

    const familyServiceKeys = services.map(service => service.key);
    // Peça em nova solicitação só para o cliente: é ele quem confirma e é cobrado (a API recusa os demais).
    const linkedServices = !loginService.isCustomer() ? [] : LINKABLE_SERVICE_KEYS
        .filter(key => !familyServiceKeys.includes(key))
        .map(key => ({ key, name: requestServices.structure[key].name }));
    const serviceOptions = [...services, ...linkedServices];
    const isLinkedService = linkedServices.some(service => service.key === newService.key);

    useEffect(() => {

        if (newService.key == requestServices.ISSUEPAYMENTFEE) {

            requestRegistryService
                .getFieldsToCreateIssuePaymentServiceAfterConclusion(request.id)
                .then((res) => setAdditionalFields(res.fields));

            return;
        }

        if (newService.key == requestServices.PETITION && !!newService.forwardModalityId && !isLinkedService) {

            requestFieldsConfig
                .getFields(request.areaId, newService.forwardModalityId, request)
                .then(({ premiumServices, ...otherFields }) => {

                    const allFields = Object.values(otherFields).flatMap(i => i);
                    setAdditionalFields(allFields);

                    const newServiceWithFields = {
                        ...newService,
                        fields: allFields
                            .map(field => ({
                                ...field,
                                value: request[field.key] || (request.fields || {})[field.key]?.value,
                            }))
                            .reduce((acc, cur) => ({ ...acc, [cur.key]: cur }), {})
                    }
                    setNewService(newServiceWithFields);
                });

            return;
        }

    }, [newService.key, newService.forwardModalityId]);

    const redirectToRequest = () => history.push(`/solicitations/${mainRequestId}`);

    const handleChangeService = (field, fieldValue) =>
        setNewService((values) => ({ ...values, [field]: fieldValue }));

    const validationByServiceKey = {
        [requestServices.PETITION]: () => !!newService.forwardModalityId,
        [requestServices.CALCULATION]: () => !!newService.calculationServiceId,
        [requestServices.ISSUEPAYMENTFEE]: () => !!(newService.fields || {})?.issueFeeQuantity?.value
    }

    // Nova solicitação vinculada: abre o cadastro dela pré-preenchido; a cobrança é no finalizar.
    // O card das peças prontas monta ao voltar e carrega a lista depois: espera ele aparecer.
    const scrollToSuggestions = (attempt = 0) => {
        const card = document.querySelector('[data-testid="suggested-requests"]');
        if (card && card.offsetHeight > 0)
            return card.scrollIntoView({ behavior: 'smooth' });
        if (attempt < 20)
            setTimeout(() => scrollToSuggestions(attempt + 1), 150);
    }

    const createLinkedService = async (duplicateReason = null) => {
        const serviceId = requestServices.structure[newService.key].id;
        await createLinkedRequest(mainRequestId, {
            serviceId,
            ...(newService.key === requestServices.PETITION ? { modalityId: newService.forwardModalityId } : {}),
            ...(duplicateReason ? { allowDuplicate: true, duplicateReason } : {}),
        })
            .then(result => history.push(`/solicitations/new/${result.requestId}`))
            .catch(async error => {
                const duplicate = duplicateOf(error);
                if (!duplicate)
                    return communicationProvider.getErrorMessage(error, null, 'Ocorreu um problema ao abrir a nova solicitação');
                // A mesma peça já está pronta ou já foi pedida: não abre um segundo pedido igual.
                const choice = await resolveDuplicate(duplicate);
                if (choice.action === 'retry')
                    return createLinkedService(choice.duplicateReason);
                if (choice.action === 'open')
                    return window.location.assign(`/solicitations/${choice.requestId}`);
                if (choice.action === 'suggestion') {
                    onCancel && onCancel();
                    scrollToSuggestions();
                }
            });
    }

    const createService = async () => {

        if (!newService.key) {
            SnackbarAlert.error('Selecione um serviço para continuar.');
            return;
        }

        const validation = validationByServiceKey[newService.key];
        if (validation && !validation()) {
            SnackbarAlert.error('Preencha todos os campos para concluir!');
            return;
        }

        if (isLinkedService)
            return createLinkedService();

        const { fields: newRequestFields, calculationServiceId, key: newServiceKey, ...requestChanges } = newService;
        const serviceId = requestServices.structure[newServiceKey].id;

        await Promise.all([
            editRequestValue(requestChanges, newRequestFields, serviceId),
            editFieldValues(newRequestFields)
        ])

        createNewService(mainRequestId, { serviceId, calculationServiceId })
            .then(() => {
                SnackbarAlert.success('Serviço criado com sucesso');
                redirectToRequest();
            })
            .catch(error => SnackbarAlert.error('Ocorreu um problema ao criar o seu serviço'))
    }

    const editRequestValue = async (requestChanges, fieldsChanges, newServiceId) => {

        const hasRequestChanges = Object.values(requestChanges).length > 0
        if (!hasRequestChanges)
            return null;

        const changes = {
            forwardModalityId: newServiceId !== requestServices.structure.petition.id ? null : requestChanges.forwardModalityId,
            processNumber: requestChanges.processNumber || fieldsChanges.processNumber?.value,
            state: requestChanges.state,
            city: requestChanges.city,
            citationDate: requestChanges.citationDate,
            servicePublishedAt: requestChanges.servicePublishedAt || null,
            freeJustice: requestChanges.freeJustice,
            hearingDateTime: requestChanges.hearingDateTime,
        }

        return solicitationService.editValue(request.id, changes);
    }

    const editFieldValues = async (newRequestFields = {}) => {

        const fieldsValues = Object.values(newRequestFields);
        if (!fieldsValues.length)
            return null;

        return Promise.all(fieldsValues.filter(f => f.id).map(field => requestFieldsConfig.editField(request, field, true)(field)));
    }

    const renderFieldTitle = (text, withMargin = true) => (
        <label class={`block text-sm font-medium text-gray-700 ${withMargin ? 'mb-2' : ''}`}>{text}</label>
    );

    const renderAdditionalFields = () => {

        return additionalFields.map((field, index) => (
            <React.Fragment key={index}>
                {requestFieldsConfig.getComponentField({
                    field,
                    request: { ...request, ...newService },
                    setValue: handleChangeService,
                    isEdit: true,
                    fieldProps: {
                        titleSize: 14,
                        spacing: 5,
                        titleWeight: 400,
                        disabled: isCreatingServices
                    },
                    fieldsWithError: [],
                    setAlert: () => { }
                })}
            </React.Fragment>
        ));
    };

    const renderFieldsByNewServiceId = () => {

        if (!newService.key)
            return null;

        const fieldsRenderFunctionByNewServiceId = {
            [requestServices.ISSUEPAYMENTFEE]: () => additionalFields.length > 0 && (
                <div className="mt-6">
                    {renderFieldTitle('2. Preencha os campos para a realização do serviço de Emissão de Guias')}

                    <div className="rounded bg-blue-50 p-6 flex flex-col gap-y-6">
                        {renderAdditionalFields()}
                    </div>
                </div>
            ),
            [requestServices.CALCULATION]: () => (
                <div className="mt-6">
                    {renderFieldTitle('2. Selecione o tipo de cálculo')}
                    <RequestCalculationServices
                        request={request}
                        requestId={request?.id}
                        areaId={request.areaId}
                        calculationServiceId={newService.calculationServiceId || ''}
                        isEdit={false}
                        setFieldValue={(field, value) => handleChangeService('calculationServiceId', value)}
                        value={newService?.calculationServiceId}
                        useCustomSelect
                        fieldProps={{ disabled: isCreatingServices }}
                    />
                </div>
            ),
            [requestServices.PETITION]: () => (
                <div className="mt-6">
                    {renderFieldTitle('2. Preencha os campos para a realização do serviço de Petição')}

                    <div className="rounded bg-blue-50 p-6">
                        <SelectModality
                            modalityId={newService.forwardModalityId}
                            setMultipleValues={(values) => {
                                setNewService((newService) => ({ ...newService, forwardModalityId: values.modalityId }));
                            }}
                            serviceId={requestServices.structure[newService.key]?.id}
                            fieldProps={{
                                titleSize: 14,
                                spacing: 5,
                                titleWeight: 400,
                                disabled: isCreatingServices,
                                style: {
                                    paddingTop: 3.5,
                                    paddingBottom: 2.5,
                                    paddingLeft: 4,
                                }
                            }}
                        />

                        {isLinkedService ? (
                            <p className="mt-4 text-sm text-gray-600">
                                Esta peça será aberta em uma nova solicitação, já preenchida com os dados deste caso. Você confere e finaliza o cadastro na próxima tela.
                            </p>
                        ) : (
                            <div className="flex flex-col gap-y-6 mt-6">
                                {renderAdditionalFields()}
                            </div>
                        )}
                    </div>
                </div>
            )
        }

        if (isLinkedService && newService.key !== requestServices.PETITION)
            return (
                <div className="mt-6 rounded bg-blue-50 p-6 text-sm text-gray-600">
                    Esta peça será aberta em uma nova solicitação, já preenchida com os dados deste caso. Você confere e finaliza o cadastro na próxima tela.
                </div>
            );

        const newServiceFieldsRenderFunction = fieldsRenderFunctionByNewServiceId[newService.key];
        if (!newServiceFieldsRenderFunction)
            return null;

        return newServiceFieldsRenderFunction();
    }

    return (
        <Card className="max-w-2xl mx-auto">
            <div className="text-2xl font-semibold">
                Adicionar Novo Serviço
            </div>

            <div className="mt-6">
                {renderFieldTitle('1. Selecione o tipo de serviço')}
                <CustomSelect
                    placeholder={<span className="text-base">Selecione um serviço</span>}
                    value={newService?.key}
                    onChange={value => {
                        setNewService({ "key": value });
                        setAdditionalFields([]);
                    }}
                    className="h-14"
                    loading={isCreatingServices}
                    allowClear
                    options={serviceOptions.map(service => ({
                        label: <span className="text-base pl-1">{service.name}</span>,
                        labelOptionRender: (
                            <div class="w-full flex items-center py-2 transition-colors">
                                <FileText className="shrink-0 text-primary !h-6 !w-6 mr-3" />
                                <div class="">
                                    <div class="font-medium">{service.name}</div>
                                    <div class="mt-1 text-sm text-gray-500">{requestServices.structure[service.key].shortDescription}</div>
                                </div>
                            </div>
                        ),
                        value: service.key,
                    }))}
                />
            </div>

            {renderFieldsByNewServiceId()}

            <div class="mt-6 bg-gray-50 rounded-lg p-4 flex items-start space-x-3">
                <HelpCircle className="text-primary h-8 w-8 mt-[-6px]" />
                <div class="text-sm text-gray-600">
                    <span class="font-medium">Precisa de ajuda?</span>
                    <p class="mt-1">Clique na caixa de seleção acima e escolha um dos serviços disponíveis. Se você escolher a opção "Cálculo", aparecerá uma nova caixa para você selecionar o tipo específico de cálculo que precisa.</p>
                </div>
            </div>

            <div className="mt-6 flex gap-3 justify-end">
                <Button
                    type="secondary"
                    onClick={onCancel}
                    disabled={isCreatingServices}
                >
                    Voltar
                </Button>
                <Button
                    type="primary"
                    onClick={createService}
                    loading={isCreatingServices}
                    disabled={isCreatingServices}
                >
                    Enviar
                </Button>
            </div>

        </Card>
    )
}